import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useContext, useEffect, useState } from "react";
import ReactPaginate from "react-paginate";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

import Toggle from "react-toggle";
import Sidebar from "../sidebar";
import { updateUser } from "./add-and-edit-user-page/form-section.service";
import { deleteUser, getUsers } from "./users-section.service";
import { AuthContext } from "../../../contexts/AuthContext";

export default function UsersSection() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const pageParam = queryParams.get("page");
  const [currentPage, setCurrentPage] = useState(
    pageParam ? parseInt(pageParam) : 1
  );
  const { user } = useContext(AuthContext);

  const queryClient = useQueryClient();

  useEffect(() => {
    if (pageParam) {
      setCurrentPage(parseInt(pageParam));
    }
  }, [pageParam]);

  const {
    isPending,
    isError,
    data: response,
    error,
  } = useQuery({
    queryKey: ["admin-users", currentPage],
    queryFn: () => getUsers({ page: currentPage }),
    placeholderData: keepPreviousData,
  });

  const handlePageChange = (selectedItem: { selected: number }) => {
    const newPage = selectedItem.selected + 1;
    setCurrentPage(newPage);
    navigate(`?page=${newPage}`);
  };

  const handleDeleteUser = async (userId: string) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      await deleteUser(userId);
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      Swal.fire("Deleted!", "The user has been deleted.", "success");
    }
  };

  const handleToggleUser = async (userId: string, isActive: boolean) => {
    await updateUser(userId, {
      isActive: !isActive,
    });
    queryClient.invalidateQueries({ queryKey: ["admin-users"] });
  };

  const isBackendUser = user?.userType === "Back end Entry User";
  const isSuperAdmin = user?.userType === "Super Admin";

  return (
    <section className="ec-page-content ec-vendor-dashboard section-space-p">
      <div className="container">
        <div className="row">
          <Sidebar />
          <div className="ec-shop-rightside col-lg-9 col-md-12">
            <div className="ec-vendor-dashboard-card space-bottom-30">
              <div className="ec-vendor-card-header">
                <h5>Users</h5>
                <div className="ec-header-btn">
                  <Link className="btn btn-lg btn-primary" to="add">
                    Add User
                  </Link>
                </div>
              </div>
              <div className="ec-vendor-card-body">
                <div className="ec-vendor-card-table">
                  <table className="table ec-table">
                    <thead>
                      <tr>
                        <th scope="col">First Name</th>
                        <th scope="col">Last Name</th>
                        <th scope="col">Username</th>
                        <th scope="col">Status</th>
                        {isSuperAdmin && (
                          <th scope="col" className="text-right">
                            Actions
                          </th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {response?.data.results.map((user: any) => (
                        <tr key={user.id}>
                          <td>
                            <span>{user.firstName}</span>
                          </td>
                          <td>
                            <span>{user.lastName}</span>
                          </td>
                          <td>
                            <span>{user.username}</span>
                          </td>
                          <td>
                            <span>
                              <Toggle
                                defaultChecked={user.isActive}
                                icons={false}
                                onChange={() => {
                                  handleToggleUser(user.id, user.isActive);
                                }}
                              />
                            </span>
                          </td>
                          {isSuperAdmin && (
                            <td className="text-right">
                              <span>
                                <Link
                                  to={`edit/${user.id}`}
                                  className="ec-action-btn mr-1"
                                >
                                  <i className="fa fa-edit fs-5"></i>
                                </Link>
                                <Link
                                  onClick={() => handleDeleteUser(user.id)}
                                  to={""}
                                  className="ec-action-btn text-danger "
                                >
                                  <i className="fa fa-trash fs-5"></i>
                                </Link>
                              </span>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="col-lg-12">
                  <ReactPaginate
                    nextLabel=">>"
                    onPageChange={handlePageChange}
                    pageRangeDisplayed={3}
                    marginPagesDisplayed={2}
                    pageCount={response?.data.totalPages}
                    forcePage={currentPage - 1}
                    previousLabel="<<"
                    pageClassName="page-item"
                    pageLinkClassName="page-link"
                    previousClassName="page-item"
                    previousLinkClassName="page-link"
                    nextClassName="page-item"
                    nextLinkClassName="page-link"
                    breakLabel="..."
                    breakClassName="page-item"
                    breakLinkClassName="page-link"
                    containerClassName="pagination"
                    activeClassName="active"
                    renderOnZeroPageCount={null}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
